/* eslint-disable prefer-arrow/prefer-arrow-functions,@typescript-eslint/explicit-function-return-type,@typescript-eslint/naming-convention */
/**
 * This is a copy of the library 'scroll-js'.
 * Source: https://github.com/markcellus/scroll-js
 *
 * Q: Why did you just copy the source code of this library?
 * A: Because the original library doesn't support the 'ScrollIntoViewOptions' property 'block'. See issue: https://github.com/markcellus/scroll-js/issues/33
 *
 * Q: Why do we even need this library?
 * A: Because the vanilla 'scrollIntoView' doesn't provide any callback or scrollEnd event.
 *
 * Q: What did you change?
 * A: Just the scrollIntoView signature and its elementScrollYPos calculation. :)
 *
 * Q: This is a bad idea!
 * A: Yes, this is indeed a terrible idea. Got any other solution?
 */
type EasingOptions = 'linear' | 'ease-in' | 'ease-out' | 'ease-in-out';

export interface ScrollToCustomOptions extends ScrollToOptions {
  duration?: number;
  easing?: EasingOptions;
}

export const DEFAULT_DURATION = 300;

export async function scrollTo(el: Element | Window, options: ScrollToCustomOptions = {}): Promise<void> {
  if (!(el instanceof Element) && !(el instanceof Window)) {
    throw new Error(`element passed to scrollTo() must be either the window or a DOM element, you passed ${el}!`);
  }

  options = sanitizeScrollToOptions(options);

  const scroll = (
    from: number,
    to: number,
    prop: string,
    startTime: number,
    duration: number | undefined = DEFAULT_DURATION,
    easeFunc: EasingFunction,
    callback: Function,
  ) => {
    window.requestAnimationFrame(() => {
      const currentTime = Date.now();
      const time = Math.min(1, (currentTime - startTime) / duration);

      if (from === to) {
        return callback ? callback() : null;
      }

      setScrollPosition(el, easeFunc(time) * (to - from) + from);

      /* prevent scrolling, if already there, or at end */
      if (time < 1) {
        scroll(from, to, prop, startTime, duration, easeFunc, callback);
      } else if (callback) {
        callback();
      }
    });
  };

  const currentScrollPosition = getScrollPosition(el);
  const scrollProperty = getScrollPropertyByElement(el);
  return new Promise((resolve) => {
    scroll(
      currentScrollPosition,
      typeof options.top === 'number' ? options.top : currentScrollPosition,
      scrollProperty,
      Date.now(),
      options.duration,
      getEasing(options.easing),
      resolve,
    );
  });
}

export function scrollIntoView(element: HTMLElement, options?: ScrollToCustomOptions) {
  validateElement(element);
  const { duration, easing } = sanitizeScrollToOptions(options);
  const scroller: Element = utils.getDocument().body;
  let currentContainerScrollYPos = window.pageYOffset;
  // Center target
  const elementScrollYPos =
    element.getBoundingClientRect().top +
    element.getBoundingClientRect().height / 2.0 -
    window.innerHeight / 2.0 -
    currentContainerScrollYPos;

  return scrollTo(scroller, {
    top: elementScrollYPos,
    left: 0,
    duration,
    easing,
  });
}

function validateElement(element?: HTMLElement) {
  if (element === undefined) {
    const errorMsg = 'The element passed to scrollIntoView() was undefined.';
    throw new Error(errorMsg);
  }
  if (!(element instanceof HTMLElement)) {
    throw new Error(`The element passed to scrollIntoView() must be a valid element. You passed ${element}.`);
  }
}

function getScrollPropertyByElement(el: Element | Window): 'scrollY' | 'scrollX' | 'scrollTop' | 'scrollLeft' {
  const props = {
    window: {
      y: 'scrollY',
      x: 'scrollX',
    },
    element: {
      y: 'scrollTop',
      x: 'scrollLeft',
    },
  };
  const axis = 'y';
  if (el instanceof Window) {
    // @ts-ignore
    return props.window[axis];
  } else {
    // @ts-ignore
    return props.element[axis];
  }
}

function sanitizeScrollToOptions(options: ScrollToCustomOptions = {}): ScrollToCustomOptions {
  if (options.behavior === 'smooth') {
    options.easing = 'ease-in-out';
    options.duration = DEFAULT_DURATION;
  }
  if (options.behavior === 'auto') {
    options.duration = 0;
    options.easing = 'linear';
  }
  return options;
}

function getScrollPosition(el: Element | Window): number {
  const document = utils.getDocument();
  if (el === document.body || el === document.documentElement || el instanceof Window) {
    return document.body.scrollTop || document.documentElement.scrollTop;
  } else {
    return el.scrollTop;
  }
}

function setScrollPosition(el: Element | Window, value: number): void {
  const document = utils.getDocument();
  if (el === document.body || el === document.documentElement || el instanceof Window) {
    document.body.scrollTop = value;
    document.documentElement.scrollTop = value;
  } else {
    el.scrollTop = value;
  }
}

export const utils = {
  // we're really just exporting this so that tests can mock the document.documentElement
  getDocument(): HTMLDocument {
    return document;
  },
};

// eslint-disable-next-line no-unused-vars
type EasingFunction = (t: number) => number;

interface EasingFunctions {
  'linear': EasingFunction;
  'ease-in': EasingFunction;
  'ease-out': EasingFunction;
  'ease-in-out': EasingFunction;
}

export const easingMap: EasingFunctions = {
  'linear'(t: number) {
    return t;
  },
  'ease-in'(t: number) {
    return t * t;
  },
  'ease-out'(t: number) {
    return t * (2 - t);
  },
  'ease-in-out'(t: number) {
    return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
  },
};

const getEasing = (easing?: EasingOptions): EasingFunction => {
  const defaultEasing = 'linear';
  const easeFunc = easingMap[easing || defaultEasing];
  if (!easeFunc) {
    const options = Object.keys(easingMap).join(',');
    throw new Error(
      `Scroll error: scroller does not support an easing option of "${easing}". Supported options are ${options}`,
    );
  }
  return easeFunc;
};
